import React, { useState, useMemo } from 'react';
import { rellenarWord, descargarComoWord, wordFirmado } from '../../services/minutasV2Service';
import { FUENTE, useTema } from './tema';
import {
  fuenteDeDatos, trabaDelFormato, trabaDelInforme, nombreLimpio
} from './descargasPosibles';
import { pdfDelDocumento } from '../../services/actaPublicada';

/**
 * AccionesReunion — todo lo que se puede hacer con una reunión, en el toolbar.
 *
 * Estuvo un tiempo detrás de un «⋯» para que la barra del título no creciera a
 * dos filas con títulos de media pantalla ("Platica sobre Inv. de Blockchain…
 * -20220909_130225-Grabación de la reunión"). El problema del menú es que
 * esconde: descargar el acta —lo que casi todo el mundo viene a hacer— pasaba a
 * costar dos clics y saber de antemano que estaba ahí dentro.
 *
 * Hoy viven en el menú «Archivo ▸ Exportar», que es donde se buscan, y no se
 * pintan aquí: este archivo las define y la pantalla decide bajo qué categoría
 * cuelgan.
 *
 * Dos grupos, porque no son la misma clase de cosa:
 *
 *   - **Ir a** — cambian de pestaña. Son instantáneas y nunca fallan.
 *   - **Descargar** — producen un archivo. Pueden tardar, pueden fallar, y
 *     pueden no estar disponibles.
 *
 * La regla que gobierna las descargas: **ninguna se apaga en silencio**. Sin
 * sitio ya para un párrafo bajo cada opción, la que no se puede usar no queda
 * muerta: se pulsa, y entonces dice por qué y lleva al arreglo. Un botón gris
 * que no responde no se distingue de uno roto.
 *
 * Al revés no: la que sale bien no dice nada. El navegador ya enseña el archivo
 * que acaba de bajar, y repetirlo era una línea en cada exportación. Solo habla
 * la que salió mal, o la que salió a medias.
 *
 * ── Dos formatos, no tres opciones ──────────────────────────────────────
 *
 * Aquí hubo un tiempo tres entradas: «Mi acta en Word», «Word» y «PDF». Las dos
 * primeras decían Word y daban documentos distintos, y elegir entre ellas exigía
 * saber algo que no venía al caso —si el acta se había generado sobre plantilla
 * o no—. Quien va a exportar sabe en qué formato lo quiere, no con qué motor se
 * fabricó.
 *
 * Ahora hay un Word y un PDF, y es `word` quien decide qué entregar:
 *
 *   - Con el acta generada delante, ese mismo documento, tal cual. Volver a
 *     fabricarlo daría uno distinto del que la persona acaba de revisar.
 *   - Sin ella pero con plantilla, se vuelca la reunión en la plantilla: logos,
 *     encabezados, fuentes y tablas intactos.
 *   - Sin ninguna de las dos, un .docx corriente con el informe.
 *
 * `pdf` sale del mismo documento siempre que se pueda. Se le pide a Office que
 * lo convierta —el acta ya está subida donde su motor la ve— y llega con la
 * plantilla intacta. Durante un tiempo se fabricó fotografiando el informe
 * porque para convertir un .docx conservando su diseño hace falta un Word de
 * verdad ejecutándose en algún sitio; lo hay, es el de Microsoft, y estaba a un
 * parámetro de distancia.
 *
 * La foto del informe sigue ahí de reserva, para las actas guardadas solo como
 * texto. No lleva el diseño de nadie, y cuando se recurre a ella se dice.
 */

// Un nombre propio para la copia que se fotografía. Sirve para dos cosas: que
// las reglas de abajo solo la toquen a ella, y que se reconozca si alguna vez
// se queda colgada en el documento.
const CAJA_PDF = 'acta-para-pdf';

/**
 * El informe en PDF.
 *
 * Se imprime desde una copia fuera de pantalla y no desde el panel visible por
 * dos razones: el panel está recortado con scroll —el PDF saldría cortado por
 * donde lo esté la vista— y en modo oscuro saldría con el fondo oscuro. Un PDF
 * se imprime siempre en negro sobre blanco, que es como se lee en papel.
 *
 * ── Por qué hay dos divs y no uno ────────────────────────────────────────
 *
 * Porque el PDF salía en blanco, y esta es la razón exacta.
 *
 * La copia llevaba encima el `position:fixed; left:-10000px` que la esconde. Y
 * html2pdf no fotografía el elemento donde está: lo CLONA dentro de un
 * contenedor suyo y le hace la foto ahí. El clon se llevaba el `fixed` puesto,
 * así que se salía del contenedor y se plantaba a diez mil píxeles de donde
 * apuntaba la cámara. El contenedor quedaba vacío y el resultado era un folio
 * limpio — 3 KB de nada, que además no se distingue de un fallo.
 *
 * Así que lo escondido es el envoltorio, que no se clona, y la copia queda en
 * flujo normal dentro de él. Medido: 3.058 bytes antes, 130.444 después.
 *
 * La regla, por si vuelve a aparecer otra variante: **lo que se va a
 * fotografiar no puede llevar nada que lo saque del flujo.** Ni `fixed`, ni
 * `absolute`, ni `transform`. Eso va en quien lo envuelve.
 */
async function imprimirPdf(html, titulo) {
  const { default: html2pdf } = await import('html2pdf.js');

  // Lo único que se esconde. No entra en la foto, así que puede llevar lo que
  // haga falta para quitarlo de en medio.
  const escondite = document.createElement('div');
  escondite.setAttribute('aria-hidden', 'true');
  escondite.style.cssText = 'position:fixed;left:-10000px;top:0;width:794px';

  // Lo que se fotografía: papel, en flujo normal.
  const caja = document.createElement('div');
  caja.id = CAJA_PDF;
  caja.style.cssText = [
    'width:794px', 'background:#ffffff', 'color:#111827',
    'font-family:Georgia, Times New Roman, serif', 'font-size:12pt', 'line-height:1.55'
  ].join(';');

  // Cada regla va atada a la caja. Suelta, esta hoja se aplicaba a la
  // aplicación entera mientras duraba la impresión: todas las tablas de la
  // pantalla cambiaban de borde durante un segundo.
  const hoja = [
    'table { width:100%; border-collapse:collapse; margin:12px 0; }',
    'td, th { border:1px solid #d1d5db; padding:6px 8px; font-size:11pt; text-align:left; }',
    'th { background:#f3f4f6; }',
    'h1 { font-size:19pt; margin:0 0 10px; }',
    'h2 { font-size:15pt; margin:18px 0 8px; }',
    'h3 { font-size:13pt; margin:14px 0 6px; }',
    'p, li { margin:0 0 8px; }',
    'ul, ol { padding-left:22px; }',
    'img { max-width:100%; }'
  ].map(regla => '#' + CAJA_PDF + ' ' + regla).join('\n');

  caja.innerHTML = '<style>' + hoja + '</style>' + html;

  escondite.appendChild(caja);
  document.body.appendChild(escondite);
  try {
    // Se pide el PDF hecho y se entrega aquí, en vez de dejar que lo guarde la
    // librería. Es la misma manera de bajar un archivo que usa el resto de la
    // aplicación —la que sabemos que funciona— y deja ver si lo que salió tiene
    // contenido o no.
    const documento = await html2pdf().set({
      margin: [14, 14, 16, 14],
      image: { type: 'jpeg', quality: 0.96 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      // Sin esto, una tabla o un párrafo pueden partirse justo en el salto de
      // página y quedar la mitad en cada hoja.
      pagebreak: { mode: ['avoid-all', 'css'] }
    }).from(caja).outputPdf('blob');

    // Un PDF de un folio con texto no baja de unas decenas de miles de bytes.
    // Por debajo de esto no hay tinta: es el fallo de arriba volviendo, y vale
    // más decirlo que entregar un archivo vacío que nadie mira hasta que lo
    // manda.
    if (!documento || documento.size < 6000) {
      throw new Error('El PDF salió vacío. Vuelve a intentarlo; si sigue igual, descárgalo en Word.');
    }

    bajarArchivo(documento, nombreLimpio(titulo) + '.pdf');
  } finally {
    document.body.removeChild(escondite);
  }
}

/** Entregar un archivo al navegador. */
function bajarArchivo(blob, nombre) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const estilos = (C) => ({
  // Mismo aspecto que los demás botones del toolbar: aquí no manda este
  // componente sino la fila donde se inserta, y una acción con estilo propio
  // parecería de otra aplicación.
  accion: (trabada) => ({
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 13px',
    background: C.panel, borderRadius: 8,
    // Discontinuo y apagado: se ve que hay algo que resolver antes, pero el
    // botón responde. Deshabilitado de verdad no se distingue de roto.
    borderWidth: 1, borderStyle: trabada ? 'dashed' : 'solid', borderColor: C.borde,
    color: trabada ? C.suave : C.texto,
    fontSize: 13, fontFamily: FUENTE, cursor: 'pointer', whiteSpace: 'nowrap'
  }),
  // Separa «Ir a» de «Descargar» sin gastar una fila en dos encabezados.
  divisor: { width: 1, alignSelf: 'stretch', background: C.borde, margin: '0 2px' },
  // Ocupa su propia línea dentro del toolbar, que ya envuelve. `order` lo manda
  // al final de la fila aunque en el DOM venga antes que «Subir otro formato»:
  // sin eso, al aparecer un aviso ese botón saltaba a una tercera línea.
  recado: (malo) => ({
    order: 1, flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 10,
    fontSize: 12.5, lineHeight: 1.5, color: malo ? C.atencion : C.suave
  }),
  arreglo: {
    padding: '4px 9px', borderRadius: 6, fontSize: 12,
    background: 'transparent', color: C.acentoClaro, fontFamily: FUENTE,
    border: '1px solid ' + C.borde, cursor: 'pointer', whiteSpace: 'nowrap'
  }
});

/**
 * @param {Object} props
 * @param {Object|null} props.minuta - la minuta con su `html`, si ya se generó
 * @param {string} props.titulo - el de la reunión, para el nombre del archivo
 * @param {Array} props.formatos - los formatos .docx del usuario
 * @param {string|number} props.formatoSel - el elegido en la pantalla
 * @param {string|Object|null} props.transcripcion - la cruda, si se tiene
 * @param {string|null} props.userId
 * @param {Object|null} props.docxListo - el .docx ya generado en esta visita
 * @param {Object|null} props.documento - el acta subida ({driveId, itemId}), si la hay
 * @param {Function} props.tokenGraph - da un token de Microsoft Graph
 * @param {Object|null} props.cita - datos del calendario (fecha, horario, organizador)
 * @param {Function} props.onPestana - para ir a otra pestaña
 */
export function useAccionesReunion({
  minuta, titulo, formatos = [], formatoSel = '', transcripcion = null, userId = null,
  docxListo = null, cita = null, onPestana = () => {},
  documento = null, tokenGraph = null
}) {

  const [trabajando, setTrabajando] = useState(null);
  const [recado, setRecado] = useState(null);

  const html = (minuta && minuta.html) || null;
  const fuente = useMemo(() => fuenteDeDatos(transcripcion, html), [transcripcion, html]);

  const irAConfig = () => { window.location.hash = '#v2/config'; };

  // Aquí se contaba cuántas zonas habían quedado rellenas y cuáles no. Se ha
  // ido con el resto de los avisos de éxito: las zonas vacías ya se ven en el
  // propio editor, señaladas una a una, que es donde se van a corregir. Decirlo
  // otra vez al descargar era repetir en una línea lo que la pantalla enseña
  // entero.

  /**
   * El acta en Word, venga de donde venga.
   *
   * Tres procedencias, en orden de lo más fiel a lo menos. La primera manda
   * siempre: si hay un documento generado, es el que la persona tiene delante y
   * el que espera que le llegue. Volver a fabricarlo daría otro.
   */
  const enWord = async () => {
    if (docxListo) {
      docxListo.descargar();
      return;
    }

    // Sin acta generada pero con plantilla: se vuelca la reunión dentro.
    if (!trabaDelFormato({ formatos, formatoSel, fuente })) {
      const resultado = await rellenarWord({
        formatId: Number(formatoSel),
        transcripcion: fuente,
        cita,
        userId
      });
      resultado.descargar();
      return;
    }

    // Ni acta ni plantilla: un documento corriente con el informe. No lleva el
    // diseño de nadie, pero es un Word y se puede mandar.
    await descargarComoWord({ html, titulo: nombreLimpio(titulo) });
  };

  /**
   * El acta en PDF.
   *
   * Del documento siempre que se pueda: lo convierte Office y llega con la
   * plantilla puesta. Si no hay documento subido —un acta guardada solo como
   * texto, o una subida que aún no terminó— queda la foto del informe, que sirve
   * pero no lleva el diseño.
   *
   * Cuando se recurre a la reserva se dice. Entregar un PDF sin la plantilla
   * callando que lo es tiene el mismo defecto que entregarlo en blanco: no se
   * nota hasta que alguien lo abre, o hasta que lo manda.
   */
  /**
   * El acta en Word con las rubricas dibujadas.
   *
   * El servidor lo rehace al pedirlo —misma plantilla, mismo contenido por
   * zona, mas las firmas— en vez de guardarlo firmado: el acta sigue viva
   * mientras se firma, y un documento congelado empezaria a diferir del de
   * verdad sin que nada lo dijera.
   */
  const enWordFirmado = async () => {
    const r = await wordFirmado(minuta.minuteId, userId);

    const bytes = Uint8Array.from(atob(r.contentBase64), c => c.charCodeAt(0));
    bajarArchivo(
      new Blob([bytes], { type: r.contentType }),
      r.fileName || (nombreLimpio(titulo) + ' (firmada).docx')
    );

    // Quien firmo pero no tiene fila en la tabla de asistentes. Su rubrica no
    // esta en el documento que se acaba de bajar, y entregarlo sin decirlo seria
    // dar por completa un acta a la que le falta una firma.
    if (r.withoutRow && r.withoutRow.length > 0) {
      return `${r.withoutRow.join(', ')} ${r.withoutRow.length === 1 ? 'firmó' : 'firmaron'} `
        + 'pero no aparece en la tabla de asistentes, así que su firma no está en el '
        + 'documento. Añádelo a los asistentes y vuelve a descargarlo.';
    }
    return null;
  };

  const enPdf = async () => {
    if (documento && tokenGraph) {
      try {
        const pdf = await pdfDelDocumento(await tokenGraph(), documento);
        bajarArchivo(pdf, nombreLimpio(titulo) + '.pdf');
        return;
      } catch (e) {
        await imprimirPdf(html, titulo);
        return 'Descargado, pero sin tu plantilla: Office no pudo convertir el documento ' +
          '(' + (e.mensajeUsuario || e.message) + '). Para el acta con su diseño, descárgala en Word.';
      }
    }

    await imprimirPdf(html, titulo);
  };

  const GRUPOS = [
    {
      titulo: 'Ir a',
      opciones: [
        {
          id: 'buscar',
          icono: '🔍',
          nombre: 'Buscar en la transcripción',
          // En el toolbar manda el ancho; el nombre entero va en el tooltip.
          etiqueta: 'Buscar',
          detalle: 'Abre la transcripción para buscar lo que se dijo y saltar al minuto.',
          instantanea: true,
          hacer: () => onPestana('transcripcion')
        },
        {
          id: 'correo',
          icono: '✉',
          nombre: 'Redactar correo',
          etiqueta: 'Correo',
          detalle: 'Prepara un correo de seguimiento con los acuerdos de la reunión.',
          instantanea: true,
          hacer: () => onPestana('email')
        }
      ]
    },
    {
      titulo: 'Descargar',
      opciones: [
        {
          id: 'word',
          icono: '⤓',
          nombre: 'Word',
          detalle: docxListo
            ? 'El documento que estás viendo, tal cual: logos, encabezados y tablas incluidos.'
            : 'El acta en .docx, sobre tu plantilla si tienes una.',
          // Con el documento ya generado no hay nada que comprobar: existe, se
          // está viendo, y descargarlo es entregar ese archivo. Sin él queda la
          // salida de siempre —el informe— y para eso basta que haya informe.
          traba: () => (docxListo ? null : trabaDelInforme(html)),
          hacer: enWord
        },
        {
          id: 'word-firmado',
          icono: '🖋',
          nombre: 'Word firmado',
          detalle: 'El acta con las rúbricas que dibujó cada firmante, en su fila de '
            + 'la tabla de asistentes.',
          // Sin traba a proposito. Quien sabe si hay rubricas es el servidor
          // —estan en la base, no aqui— y preguntarselo al abrir el menu seria
          // una llamada por cada vez que alguien pasa por «Exportar». Si no las
          // hay, responde diciendolo y el recado lo cuenta: es la misma regla de
          // esta pantalla, no apagarse en silencio.
          hacer: enWordFirmado
        },
        {
          id: 'pdf',
          icono: '🖨',
          nombre: 'PDF',
          detalle: documento
            ? 'El acta tal cual, convertida por Office: conserva tu plantilla.'
            : 'El informe en PDF. Sin el diseño de tu plantilla.',
          // Con documento subido no hace falta informe: se convierte el acta.
          traba: () => (documento ? null : trabaDelInforme(html)),
          hacer: enPdf
        }
      ]
    }
  ];

  const lanzar = async (opcion) => {
    // Cambiar de pestaña no produce nada que contar: se hace y ya está.
    if (opcion.instantanea) {
      setRecado(null);
      opcion.hacer();
      return;
    }

    // Aquí es donde se cumple lo de no apagarse en silencio: la acción que no
    // puede usarse responde al clic explicando qué falta, en vez de ignorarlo.
    const traba = opcion.traba ? opcion.traba() : null;
    if (traba) {
      setRecado({ texto: traba.texto, malo: true, arreglo: traba.arreglo });
      return;
    }

    setRecado(null);
    setTrabajando(opcion.id);
    try {
      // ── Lo que sale bien no se anuncia ────────────────────────────────
      //
      // Una descarga que funciona ya se anuncia sola: el navegador enseña el
      // archivo abajo y ahí está. Poner encima «Descargado, con las 14 zonas
      // rellenas» era una línea que aparecía en cada exportación, ocupaba sitio
      // en la barra y no decía nada que no supiera ya quien acababa de pulsar.
      //
      // Lo que no se ve solo es lo contrario, y eso sigue igual: si la descarga
      // revienta, o si falta algo para poder hacerla, el recado aparece y lo
      // dice. La regla de esta pantalla no era «avisar siempre», era «no
      // apagarse en silencio».
      // Lo normal es que no devuelva nada. Cuando devuelve algo es un pero: se
      // hizo, pero no como se esperaba, y eso hay que decirlo.
      const pero = await opcion.hacer();
      if (pero) setRecado({ texto: pero, malo: true });
    } catch (e) {
      setRecado({ texto: e.mensajeUsuario || e.message || 'No se pudo descargar.', malo: true });
    } finally {
      setTrabajando(null);
    }
  };

  // Las opciones, listas para colgar de un menu. Se devuelven en vez de
  // pintarse porque quien decide como se agrupan es la pantalla: «Exportar»
  // vive bajo «Archivo» junto a publicar y subir formato, que no son de aqui.
  const comoElementos = (titulo) => {
    const grupo = GRUPOS.find(g => g.titulo === titulo);
    if (!grupo) return [];
    return grupo.opciones.map(opcion => ({
      id: opcion.id,
      etiqueta: trabajando === opcion.id
        ? 'Preparando…'
        : (opcion.nombre || opcion.etiqueta),
      icono: opcion.icono,
      detalle: opcion.detalle,
      traba: opcion.traba || null,
      hacer: () => lanzar(opcion)
    }));
  };

  return { elementosDe: comoElementos, recado, limpiarRecado: () => setRecado(null), irAConfig };
}

/**
 * Lo que salió mal: la descarga que reventó, o lo que falta para poder hacerla.
 * Lo que sale bien no pasa por aquí: una descarga que funciona ya se anuncia
 * sola en el navegador.
 *
 * Se pinta aparte de los menús porque el menú se cierra al pulsar y el mensaje
 * tiene que quedarse.
 */
export const RecadoDeAcciones = ({ recado, irAConfig }) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);
  if (!recado) return null;

  return (
    <div style={S.recado(recado.malo)} role="status">
      <span>{recado.texto}</span>
      {/* Toda traba con arreglo se arregla en Configuración: es donde se suben
          las actas y donde se ven sus zonas. */}
      {recado.arreglo && (
        <button style={S.arreglo} onClick={irAConfig}>{recado.arreglo}</button>
      )}
    </div>
  );
};

export default useAccionesReunion;
